import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { useSendToCustomerContact } from "@/components/send-to-customer/use-send-to-customer-contact";
import { validateSendToCustomerContact } from "@/components/send-to-customer/validate-send-to-customer";
import { resolveSendToCustomerMessageType } from "@/components/send-to-customer/resolve-message-type";
import {
  getWhatsAppMessageByBoardMissionIdApi,
  sendJobExampleConfirmationToClientApi,
} from "@/services/api-service/production-floor/production-floor-endpoints";
import { useWhatsAppWeb } from "@/pages-components/quote-new/use-whats-app-web";

export interface IProofApprovalMission {
  id?: string;
  productType?: string;
  customerID?: string;
  customerId?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export const boardMissionDetailsToProofApprovalMission = (
  boardMissions: {
    boardMissionId?: string;
    productType?: string;
    clientId?: string;
    contactName?: string;
    clientPhone?: string;
    clientEmail?: string;
    contactEmail?: string;
  } | null
  | undefined,
): IProofApprovalMission => ({
  id: boardMissions?.boardMissionId,
  productType: boardMissions?.productType,
  customerID: boardMissions?.clientId,
  contactName: boardMissions?.contactName,
  contactPhone: boardMissions?.clientPhone,
  contactEmail: boardMissions?.contactEmail || boardMissions?.clientEmail,
});

const useSendProofApprovalToCustomer = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const { alertFault, alertSuccessSend, alertFaultSend } = useSnackBar();
  const { openWhatsAppWebLink } = useWhatsAppWeb();
  const [openModal, setOpenModal] = useState(false);
  const [selectedMission, setSelectedMission] =
    useState<IProofApprovalMission | null>(null);
  const [isSending, setIsSending] = useState(false);

  const clientId =
    selectedMission?.customerID || selectedMission?.customerId || null;

  const {
    state,
    isLoadingContacts,
    onChangeField,
    onNotificationChange,
    resetState,
  } = useSendToCustomerContact({
    open: openModal,
    clientId,
    defaultContactName: selectedMission?.contactName || "",
    defaultContactPhone: selectedMission?.contactPhone || "",
    defaultContactMail: selectedMission?.contactEmail || "",
  });

  const onOpenModal = useCallback((mission: IProofApprovalMission) => {
    setSelectedMission(mission);
    setOpenModal(true);
  }, []);

  const onCloseModal = useCallback(() => {
    if (isSending) {
      return;
    }
    setOpenModal(false);
    setSelectedMission(null);
    resetState();
  }, [isSending, resetState]);

  const handleSend = useCallback(async () => {
    const boardMissionId = selectedMission?.id;
    if (!boardMissionId || isSending) {
      return;
    }

    const validationError = validateSendToCustomerContact(state, t);
    if (validationError) {
      alertFault(validationError);
      return;
    }

    const messageType = resolveSendToCustomerMessageType(
      state.sendByMail,
      state.sendBySms,
    );
    const shouldSendByApi = messageType !== null;
    const shouldSendByWhatsApp = state.sendByWhatsapp;

    setIsSending(true);

    try {
      if (shouldSendByApi) {
        const apiSuccess = await new Promise<boolean>((resolve) => {
          sendJobExampleConfirmationToClientApi(
            callApi,
            (res) => resolve(!!res?.success),
            {
              documentId: boardMissionId,
              messageType,
              contactPhone: state.contactPhone?.trim(),
              contactEmail: state.contactMail?.trim(),
            },
          );
        });

        if (!apiSuccess) {
          alertFaultSend();
          return;
        }
      }

      if (shouldSendByWhatsApp) {
        const whatsAppSuccess = await new Promise<boolean>((resolve) => {
          getWhatsAppMessageByBoardMissionIdApi(
            callApi,
            (res) => {
              if (res?.success) {
                openWhatsAppWebLink(
                  res?.data,
                  state.contactPhone?.trim() || "",
                  true,
                );
                resolve(true);
              } else {
                resolve(false);
              }
            },
            boardMissionId,
          );
        });

        if (!whatsAppSuccess) {
          alertFaultSend();
          return;
        }
      }

      alertSuccessSend();
      onSuccess?.();
      setOpenModal(false);
      setSelectedMission(null);
      resetState();
    } finally {
      setIsSending(false);
    }
  }, [
    selectedMission,
    isSending,
    state,
    t,
    callApi,
    alertFault,
    alertFaultSend,
    alertSuccessSend,
    openWhatsAppWebLink,
    onSuccess,
    resetState,
  ]);

  return {
    openModal,
    isSending,
    isLoadingContacts,
    state,
    onOpenModal,
    onCloseModal,
    onChangeField,
    onNotificationChange,
    handleSend,
  };
};

export { useSendProofApprovalToCustomer };
